/**
 * The two waits between a WHEP answer and a picture — and what the app says
 * when they end without one (card `t_20b35b28`).
 *
 * Measured on the owner's dev box with **Chrome 153 at defaults** and a
 * containerised go2rtc (`docs/plans/evidence/mdns-ice-go2rtc-t_c1687051.md`,
 * §1 and §4): the app's own handshake
 * (`composables/useCameraStreamSession.ts#connect`) posts a WHEP offer that
 * go2rtc accepts — and then ICE stays `checking` **forever**:
 *
 *     iceConnectionState=checking          (never leaves 'checking')
 *     connectionState=connecting @1125ms
 *     result: {"ok":false,"connectionState":"connecting","iceConnectionState":"checking","videoReadyState":0}
 *
 * Nothing rejects and nothing times out, so the live view spins in `connecting`
 * for as long as the page is open and the person is never told anything:
 * `ok:false` existed only in a harness result. The same handshake on a path
 * that does work reaches `iceConnectionState=connected` at **2939 ms** and
 * `connectionState=connected` at **2947 ms** (§4) — that measurement is what
 * `MEDIA_PATH_BUDGET_MS` below is sized against.
 *
 * Why a module rather than code inside one player: the same handshake is
 * written twice in this app — the shared session
 * (`composables/useCameraStreamSession.ts`, behind `components/WebRTCPlayer.vue`
 * and every surface that mounts it) and the intercom's own copy
 * (`components/IntercomPlayer.vue`, a voice surface). A bound only one of them
 * gets is a bug in the other, so the rule lives here and both call it.
 *
 * Two waits, one question each:
 *
 *  - `gatherIce` — the ICE gathering window. The app gathers for a fixed window
 *    and does not trickle (`connect()` has no `onicecandidate` writer), so the
 *    offer carries whatever was gathered by the time the window closed. This
 *    function returns **what the window saw** (a candidate census by type, and
 *    whether gathering had finished) instead of a bare promise, because a
 *    window that closes while gathering is still open can silently produce a
 *    relay-less offer — and that failure looks *exactly* like the one above
 *    (measured: §111, relay candidates arriving around the window's edge).
 *  - `watchMediaPath` — after the answer: how long does `connectionState` get
 *    to reach `connected`, and what is that ending called when it never does.
 */

/** The ICE gathering window `connect()` waits before it posts its offer. */
export const ICE_GATHER_WINDOW_MS = 2_000;

/**
 * How long a WHEP answer gets to turn into a live media path.
 *
 * Sized against the measurement in the header, not against a feeling: the
 * working path on this box reaches `connectionState=connected` at 2947 ms
 * **from page load**, of which the ICE gathering window is 2 s and the WHEP
 * round trip a few hundred ms — the answer is applied at ~1125 ms, so ICE
 * itself needed ~1.8 s on the TURN-relay path and ~0.3 s on a run whose
 * candidates were ready at the WHEP POST. A direct LAN pair (the path a
 * host-networked go2rtc gives) is faster than either.
 *
 * 8 s is therefore ~2.7× the slowest measured healthy path from page load and
 * ~4.4× its own start (the answer): comfortably above anything that works, and
 * still short enough that a person waiting on a spinner learns something
 * instead of nothing. It is *not* a network timeout — a path that comes up
 * later than the budget is a path this app has already named as failed, and the
 * surface's Retry is the way back.
 */
export const MEDIA_PATH_BUDGET_MS = 8_000;

/** Local ICE candidates, counted by type. */
export interface IceCandidateCensus {
  total: number;
  host: number;
  srflx: number;
  relay: number;
  prflx: number;
}

/** What one ICE gathering window saw. */
export interface IceGathering {
  /** Did gathering finish inside the window? `false` = the offer went out truncated. */
  complete: boolean;
  /** How long the window ran, ms. */
  waitedMs: number;
  candidates: IceCandidateCensus;
}

/** A candidate's type, from the candidate itself or its SDP line. */
function candidateType(candidate: RTCIceCandidate): string | null {
  const direct = (candidate as { type?: string }).type;
  if (direct) return direct;
  const match = /\btyp\s+([a-z0-9]+)/i.exec(candidate.candidate || '');
  return match ? match[1].toLowerCase() : null;
}

function emptyCensus(): IceCandidateCensus {
  return { total: 0, host: 0, srflx: 0, relay: 0, prflx: 0 };
}

/**
 * Wait for this connection's ICE gathering, and report what it gathered.
 *
 * Resolves as soon as `iceGatheringState` is `complete`, or when the window
 * expires — whichever comes first, exactly like the inline wait it replaces —
 * and returns the census either way. Never throws: a peer connection that has
 * already gathered (or that is closed underneath the caller) answers with the
 * state it found.
 */
export function gatherIce(
  pc: RTCPeerConnection,
  windowMs: number = ICE_GATHER_WINDOW_MS,
): Promise<IceGathering> {
  const candidates = emptyCensus();
  const started = Date.now();

  const onCandidate = (event: RTCPeerConnectionIceEvent) => {
    const candidate = event.candidate;
    // The null candidate is the end-of-gathering marker, not a candidate.
    if (!candidate) return;
    candidates.total += 1;
    const type = candidateType(candidate);
    if (type === 'host' || type === 'srflx' || type === 'relay' || type === 'prflx') {
      candidates[type] += 1;
    }
  };

  pc.addEventListener('icecandidate', onCandidate);

  const settled = new Promise<void>((resolve) => {
    if (pc.iceGatheringState === 'complete') {
      resolve();
      return;
    }

    const timer = setTimeout(resolve, windowMs);
    const onGatheringStateChange = () => {
      if (pc.iceGatheringState !== 'complete') return;
      clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', onGatheringStateChange);
      resolve();
    };
    pc.addEventListener('icegatheringstatechange', onGatheringStateChange);
  });

  return settled.then(() => {
    pc.removeEventListener('icecandidate', onCandidate);
    return {
      complete: pc.iceGatheringState === 'complete',
      waitedMs: Date.now() - started,
      candidates,
    } satisfies IceGathering;
  });
}

/** How the wait for a live media path ended. */
export type MediaPathOutcome =
  /** `connectionState` reached `connected`: media can flow. */
  | 'live'
  /** The budget expired with the connection still not up — the measured symptom. */
  | 'no-media-path'
  /** ICE itself gave up (`connectionState === 'failed'`). */
  | 'failed'
  /** The connection was closed — by the app (a teardown) or by the browser. */
  | 'closed';

export interface MediaPathWatch {
  /** Resolves once the question is answered. Never rejects. */
  outcome: Promise<MediaPathOutcome>;
  /** Stop watching: the caller tore this connection down and owns the state now. */
  cancel: () => void;
}

/**
 * Watch one peer connection until it is live or the budget runs out.
 *
 * Start it **after** the WHEP answer is applied — the budget is the answer's
 * budget, not the whole handshake's — and `cancel()` it on teardown, or a
 * connection that was closed on purpose would resolve as a failure.
 *
 * Reads `connectionState` (the same signal both players already branch on), so
 * `connected` means ICE completed *and* DTLS is up. `disconnected` is not an
 * ending: ICE can come back from it, and only the budget decides that case.
 */
export function watchMediaPath(pc: RTCPeerConnection, budgetMs: number = MEDIA_PATH_BUDGET_MS): MediaPathWatch {
  let settle: (outcome: MediaPathOutcome) => void = () => undefined;
  const outcome = new Promise<MediaPathOutcome>((resolve) => {
    settle = resolve;
  });

  let done = false;

  const onConnectionStateChange = () => {
    const state = pc.connectionState;
    if (state === 'connected') finish('live');
    else if (state === 'failed') finish('failed');
    else if (state === 'closed') finish('closed');
  };

  const timer = setTimeout(() => finish('no-media-path'), budgetMs);

  function finish(result: MediaPathOutcome) {
    if (done) return;
    done = true;
    clearTimeout(timer);
    pc.removeEventListener('connectionstatechange', onConnectionStateChange);
    settle(result);
  }

  pc.addEventListener('connectionstatechange', onConnectionStateChange);
  // The answer may already have completed before the caller got here.
  onConnectionStateChange();

  return { outcome, cancel: () => finish('closed') };
}

/**
 * Why an attempt ended **without** a media path (card `t_4d5b3263`).
 *
 * `connected` is not a failure and is not a member: these three are the whole
 * set a surface may name, and each one is a different next action for the
 * person reading the screen — a spent budget is usually the hub's network
 * (measured: a relay-less offer against a bridged go2rtc, §111), `failed` is an
 * ICE that gave up outright, and `no-candidates` is a device that never
 * gathered anything to offer at all (unreachable in a real browser: Chrome
 * always gathers at least mDNS host candidates — unit-tested only).
 */
export type MediaPathFailureReason = 'budget' | 'failed' | 'no-candidates';

/**
 * The catalogue key for each failure reason. Both players take their cause line
 * from this one map — the same reason the two waits live in this module: a
 * bound (or a wording) one surface gets and the other does not is a bug in the
 * other. `src/services/mediaPath.test.ts` pins the map against all six
 * catalogues, so a missing translation is a test failure, not a raw key path on
 * a screen.
 */
export const MEDIA_PATH_REASON_KEYS = {
  budget: 'views.cameras.mediaPathReasonBudget',
  failed: 'views.cameras.mediaPathReasonFailed',
  'no-candidates': 'views.cameras.mediaPathReasonNoCandidates',
} as const satisfies Record<MediaPathFailureReason, string>;

/**
 * The catalogue key to paint for a reason, or `null` when there is nothing to
 * name — a live path, no verdict yet, or a failure that is not about the media
 * path at all (those carry their own sentence).
 */
export function mediaPathReasonKey(
  reason: MediaPathFailureReason | 'connected' | null | undefined,
): string | null {
  if (reason === 'budget' || reason === 'failed' || reason === 'no-candidates') {
    return MEDIA_PATH_REASON_KEYS[reason];
  }
  return null;
}
